import { get, post, del } from './apiClient';

export interface KeyInfo {
    createdDate?: string;
    expiresOn?: string;
    ageInDays?: number;
    isActive?: boolean;
    algorithm?: string;
}

export interface KeyStatus {
    serviceName: string;
    currentKey: KeyInfo | null;
    previousKey: KeyInfo | null;
    needsRotation: boolean;
    vaultProvider?: string;
    error?: string;
}

export interface KeyOpResult {
    success: boolean;
    serviceName: string;
    keyVersion?: string;
    newKeyVersion?: string;
    keyId?: string;
    algorithm?: string;
    createdAt?: string;
    expiresAt?: string;
    rotatedAt?: string;
    cleanedKeysCount?: number;
    error?: string;
    // Rotate-only. IdentityProvider answers HTTP 200 + success:true even when it
    // did nothing, so `success` alone must never be read as "the key rotated".
    // wasRotated === false + reason === 'not_yet_due' is the no-op case; dueAt is
    // when the scheduler would rotate it on its own.
    wasRotated?: boolean;
    reason?: string;
    dueAt?: string;
}

export interface VaultCheckResult {
    vaultUrl: string;
    tenantId: string;
    initialized: number;
    total: number;
    results: Record<string, { current: boolean; previous: boolean; error?: string }>;
}

// ── Rotation status types ─────────────────────────────────────────────────────

export interface RotationPairStatus {
    tenantId: string;
    serviceName: string;
    lastRotationAt: string | null;
    lastRotationOutcome: string;
    lastRotationFailedStep: string | null;
    lastDryRunAt: string | null;
    lastDryRunHealthy: boolean;
    lastDryRunFailedStep: string | null;
    updatedAt: string;
}

export interface RotationHealthSummary {
    lastHealthCheckAt: string | null;
    lastHealthCheckHealthy: boolean;
    minDaysToExpiry: number;
    rotationAborted: boolean;
}

export interface KeyRotationStatusResponse {
    pairs: RotationPairStatus[];
    healthSummary: RotationHealthSummary;
}

/**
 * Which tenant's BFF actually answered the rotation-status call, echoed back by the server.
 * The IdentityProvider endpoint behind it is cross-tenant, so the pairs it returns are NOT
 * scoped to this tenant - this says only whose BFF and Keycloak config authenticated the call.
 */
export interface RotationStatusProxiedVia {
    tenantId: string;
    bffUrl: string;
}

// ── Status — direct vault (pass MongoDB _id) ──────────────────────────────────
export const getAllStatuses = (mongoId: string) =>
    get<{ success: boolean; tenantId: string; data: Record<string, KeyStatus> }>(
        `/key-management/vault-check/${mongoId}`
    );

export const getStatus = (mongoId: string, service: string) =>
    get<{ success: boolean; data: KeyStatus }>(
        `/key-management/${mongoId}/services/${service}/status`
    );

// ── Mutations — BFF → Dapr → IDP (pass tenantId string) ──────────────────────
export const initializeKey = (tenantId: string, service: string) =>
    post<{ success: boolean; data: KeyOpResult }>(`/key-management/${tenantId}/services/${service}/initialize`, {});

export const initializeAll = (tenantId: string, services?: string[]) =>
    post<{ success: boolean; initialized: number; total: number; results: Record<string, KeyOpResult> }>(
        `/key-management/${tenantId}/initialize-all`,
        services ? { services } : {}
    );

// force=true bypasses IdentityProvider's "not yet due" schedule check. Sent as a
// query param (not a body field) to match the [FromQuery] bool force that the IDP
// controller already exposes, and the ?force=true the WebBFFUI tab already sends.
export const rotateKey = (tenantId: string, service: string, force = false) =>
    post<{ success: boolean; data: KeyOpResult }>(
        `/key-management/${tenantId}/services/${service}/rotate${force ? '?force=true' : ''}`,
        {}
    );

export const cleanupKeys = (tenantId: string, service: string) =>
    del<{ success: boolean; data: KeyOpResult }>(`/key-management/${tenantId}/services/${service}/cleanup`);

// ── Rotation status — cross-tenant read ───────────────────────────────────────
/**
 * Cross-tenant in what it RETURNS, tenant-bound in how it AUTHENTICATES.
 *
 * The BFF route needs a tenantId to resolve the Keycloak realm/client for the key-admin token
 * exchange, and the AppManagement controller picks a tenant on its own when none is supplied.
 * That fallback is how a development session silently reported a demo tenant's rotation health,
 * so callers pass the tenant the user actually has selected. `proxiedVia` in the response names
 * whichever tenant ended up answering.
 */
export const getKeyRotationStatus = (tenantId?: string) =>
    get<{ success: boolean; data: KeyRotationStatusResponse; proxiedVia?: RotationStatusProxiedVia }>(
        `/key-management/rotation/status${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`
    );
